import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MicrophoneStage, Quotes, ShareNetwork, Trophy } from "@phosphor-icons/react";

export default function Comedy() {
  const { user, login } = useAuth();
  const [bits, setBits] = useState([]);
  const [hof, setHof] = useState([]);
  const [sort, setSort] = useState("top");
  const [reacted, setReacted] = useState({});

  const load = (s) => api.get("/comedy", { params: { sort: s } }).then((r) => setBits(r.data));

  useEffect(() => {
    load(sort);
  }, [sort]);

  useEffect(() => {
    api.get("/comedy", { params: { sort: "top" } }).then((r) => setHof(r.data.slice(0, 3)));
  }, [bits]);

  useEffect(() => {
    if (user) {
      api.get("/comedy/my-reactions")
        .then((r) => setReacted(Object.fromEntries(r.data.bit_ids.map((id) => [id, true]))))
        .catch((error) => console.error("Failed to load your reactions:", error));
    } else {
      setReacted({});
    }
  }, [user]);

  const react = async (bit) => {
    if (!user) {
      toast.info("Sign in to react", { description: "Your laughs count toward the Hall of Fame." });
      return login();
    }
    try {
      const res = await api.post(`/comedy/${bit.id}/react`);
      const { reacted: now, bit: updated } = res.data;
      setReacted((r) => ({ ...r, [bit.id]: now }));
      setBits((prev) => prev.map((b) => (b.id === bit.id ? { ...b, laughs: updated.laughs } : b)));
    } catch {
      toast.error("Couldn't register that laugh");
    }
  };

  const share = async (bit) => {
    const url = `${window.location.origin}/comedy#${bit.id}`;
    const text = `"${bit.content}" — ${bit.author}, via D&J's Comedy Club`;
    try {
      if (navigator.share) {
        await navigator.share({ title: bit.title, text, url });
      } else {
        await navigator.clipboard.writeText(`${text}\n${url}`);
        toast.success("Bit copied to clipboard!");
      }
    } catch {}
  };

  return (
    <div className="min-h-screen">
      <section className="relative py-24 overflow-hidden grid-bg">
        <img src="https://images.unsplash.com/photo-1543584756-8f40a802e14f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05050A]/60 to-[#05050A]" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8">
          <div className="flex items-center gap-3 mb-5">
            <MicrophoneStage weight="fill" size={22} className="text-[#FF00AA]" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF00AA]">The Comedy Section</span>
          </div>
          <h1 className="font-display font-black text-4xl lg:text-7xl tracking-tighter uppercase max-w-3xl leading-[0.95]">
            Bits, one-liners &amp; <span className="text-[#FF00AA] text-glow-cyan">green-room chaos</span>
          </h1>
          <p className="text-white/60 text-lg mt-6 max-w-xl">
            Fresh material from D&amp;J. React with a laugh, share your favourites — the funniest bits rise to the top.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 pb-24">
        {/* Hall of Fame */}
        {hof.length > 0 && hof[0].laughs > 0 && (
          <div className="glass rounded-2xl p-6 lg:p-8 mb-12 border-[#FFEB3B]/20" data-testid="hall-of-fame">
            <div className="flex items-center gap-2 mb-6">
              <Trophy weight="fill" size={22} className="text-[#FFEB3B]" />
              <span className="font-display font-black uppercase tracking-tight text-lg">Hall of Fame</span>
              <span className="text-white/40 text-xs uppercase tracking-wider">— all-time funniest</span>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {hof.map((c, i) => {
                const medal = ["#FFEB3B", "#C0C0C0", "#CD7F32"][i];
                return (
                  <div key={c.id} data-testid={`hof-${i}`} className="relative bg-[#05050A] border border-white/10 rounded-lg p-5 overflow-hidden">
                    <span className="absolute -right-3 -top-3 font-display font-black text-6xl opacity-10" style={{ color: medal }}>
                      {i + 1}
                    </span>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-7 h-7 rounded-full flex items-center justify-center font-display font-black text-sm text-[#05050A]" style={{ background: medal }}>
                        {i + 1}
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-white/40">{c.category}</span>
                    </div>
                    <h4 className="font-display font-bold text-base mb-1.5 truncate">{c.title}</h4>
                    <p className="text-white/60 text-sm line-clamp-2 leading-relaxed">"{c.content}"</p>
                    <div className="flex items-center gap-1.5 mt-3 text-[#FFEB3B] font-bold text-sm">
                      <span className="text-base leading-none">😂</span> {c.laughs}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Sort toggle */}
        <div className="flex items-center gap-2 mb-10">
          {[
            { key: "top", label: "🔥 Funniest" },
            { key: "new", label: "Newest" },
          ].map((s) => (
            <button
              key={s.key}
              data-testid={`comedy-sort-${s.key}`}
              onClick={() => setSort(s.key)}
              className={`px-5 py-2.5 rounded-sm text-sm font-bold transition-all ${
                sort === s.key
                  ? "bg-[#FF00AA] text-white shadow-[0_0_20px_rgba(255,0,170,0.4)]"
                  : "glass border border-white/15 text-white/70 hover:border-[#FF00AA]/50"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <motion.div
          initial="hidden" animate="show"
          variants={{ show: { transition: { staggerChildren: 0.05 } } }}
          className="grid md:grid-cols-2 gap-6" data-testid="comedy-list"
        >
          {bits.map((c, i) => (
            <motion.div
              key={c.id}
              id={c.id}
              variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
              className="glass rounded-lg p-8 hover:border-[#FF00AA]/50 hover:-translate-y-1 transition-all duration-300 flex flex-col"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  {sort === "top" && i < 3 && (
                    <span className="text-[10px] font-black uppercase tracking-widest text-[#FFEB3B] bg-[#FFEB3B]/10 px-2 py-1 rounded-sm">
                      #{i + 1} Top
                    </span>
                  )}
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#FF00AA] bg-[#FF00AA]/10 px-2.5 py-1 rounded-sm">
                    {c.category}
                  </span>
                </div>
                <Quotes weight="fill" size={28} className="text-white/10" />
              </div>
              <h3 className="font-display font-bold text-xl mb-3">{c.title}</h3>
              <p className="text-white/75 leading-relaxed text-[15px] flex-1">"{c.content}"</p>
              <div className="flex items-center justify-between mt-6 pt-5 border-t border-white/10">
                <span className="text-white/40 text-sm font-display">— {c.author}</span>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={`comedy-react-${c.id}`}
                    onClick={() => react(c)}
                    className={`group flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-bold transition-all ${
                      reacted[c.id]
                        ? "bg-[#FFEB3B] text-[#05050A]"
                        : "glass border border-white/15 text-white/80 hover:border-[#FFEB3B]/60 hover:text-[#FFEB3B]"
                    }`}
                    title="Laugh"
                  >
                    <span className="text-base leading-none group-hover:scale-125 transition-transform">😂</span>
                    <span data-testid={`comedy-laughs-${c.id}`}>{c.laughs}</span>
                  </button>
                  <button
                    data-testid={`comedy-share-${c.id}`}
                    onClick={() => share(c)}
                    className="p-2 rounded-full glass border border-white/15 text-white/60 hover:text-[#00E5FF] hover:border-[#00E5FF]/50 transition-colors"
                    title="Share this bit"
                  >
                    <ShareNetwork size={18} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
        {bits.length === 0 && <p className="text-white/40 py-20 text-center">No bits yet. The green room is quiet.</p>}
      </section>
    </div>
  );
}
